import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { useHeader } from 'Context/contexts/HeaderTabRightContentContext';
import PageWrapper from 'components/Common/PageWraper';
import Banner from 'components/Common/Banner';
import { useBanner } from 'components/Common/Banner/hooks/useBanner';
import SummaryTiles from 'components/Widgets/SummaryTiles/SummaryTiles';
import CustomizedTable from 'components/CustomizedTable/CustomizedTable';
import FilterDrawer from 'components/FilterDrawer/FilterDrawer';
import type { FilterState, GenericOption } from 'components/FilterDrawer/types';
import { CommonIcons, FilterIcon } from 'components/CommonIcons/Icons';
import { GroupedTableFormatter } from 'components/Common/GroupedTableFormatter';
import { useColumnFilters } from 'components/ColumnFilters/useColumnFilters';
import type { SortDirection } from 'components/ColumnFilters/types';
import RunDistribution from './RunDistribution';
import RunOvertimeTrend from './RunOvertimeTrend';
import { DashboardSplitView } from './PipelineStatusChart';
import CaptureExportControls from 'utils/CaptureExportControls';
import ImagePreview from 'utils/ImagePreview';
import { checkRole } from 'utils/roleUtils';
import ExportLimitWarningDialog from '../Modal/ExportLimitWarningDialog';
import { CUSTOM_STATUS_OPTIONS, CUSTOM_STATUS_TILES, getDefaultColumns, columnFilterPayloadToModel, toColumnFiltersPayload, toOptions, getTrend } from './utils/pipelineChartUtils';
import { useDataAssets } from './hooks/useDataAssets';
import { includeGroupByMenuColumns } from './constants/chartConstants';

const DATA_ASSETS_TILES = [
  { key: 'TotalDataAssetRuns', label: 'Total Data Assets', summaryKey: 'totalDataAssetRuns' },
  { key: 'UniqueDataAssetCount', label: 'Total Unique Data Assets', summaryKey: 'uniqueDataAssetCount' },
  { key: 'Successful', label: 'Successful', summaryKey: 'successful' },
  { key: 'Aborted', label: 'Aborted', summaryKey: 'aborted' },
  { key: 'InProgress', label: 'In Progress', summaryKey: 'inProgress' },
  { key: 'Failed', label: 'Failed', summaryKey: 'failed' },
  { key: 'Scheduled', label: 'Scheduled', summaryKey: 'scheduled' },
  ...CUSTOM_STATUS_TILES,
  { key: 'SHORT RUN', label: 'Short Run', summaryKey: 'shortRunCount' },
];

const DATA_ASSET_GROUP_BY_COLUMNS = [...includeGroupByMenuColumns, 'data_asset_name'];

const customFilterFields = [
  { label: 'Job Id', value: 'jobId' },
  { label: 'Job Name', value: 'jobName' },
  { label: 'Trigger Name', value: 'triggerName' },
  { label: 'Trigger Type', value: 'triggerType' },
  { label: 'Subscription Id', value: 'subscriptionId' },
  { label: 'Application Name', value: 'applicationName' },
  { label: 'Folder Name', value: 'folderName' },
];

const CHART_CARDS = [
  { key: 0, label: 'Run Distribution', sub: 'Latest', Icon: CommonIcons.TrendBySectorIcon },
  { key: 1, label: 'Run Trends', sub: 'All Status', Icon: CommonIcons.TrendByProgramIcon },
  { key: 2, label: 'Job Type By Status', sub: 'View All', Icon: CommonIcons.JobTypeStatusIcon },
  { key: 3, label: 'Schedule By Hour', sub: 'View All', Icon: CommonIcons.MostImpactedAppIcon },
];

const getDataAssetTableTitle = (tileKey: string) => {
  const tile = DATA_ASSETS_TILES.find(({ key }) => key === tileKey);
  if (!tile) return 'Total Data Assets';
  if (tileKey === 'TotalDataAssetRuns' || tileKey === 'UniqueDataAssetCount') {
    return tile.label;
  }
  return `${tile.label} Assets`;
};

const getAssetSummary = (rawSummary: any, rawScheduledSummary: any, rawCustomStatusSummary?: any) => {
  if (!rawSummary?.data && !rawScheduledSummary?.data) return null;

  const data = rawSummary?.data || {};
  const result = data.result || {};
  const resultLast1hr = data.resultLast1hr || {};
  const totalJobs = Object.values(result).reduce(
    (sum: number, value: any) => sum + (Number(value) || 0),
    0,
  );
  const totalJobsLastHour = Object.values(resultLast1hr).reduce(
    (sum: number, value: any) => sum + (Number(value) || 0),
    0,
  );
  const getKpi = (key: string) => {
    const total = Number(result[key]) || 0;
    const difference = Number(resultLast1hr[key]) || 0;
    return { total, difference, trend: getTrend(difference) };
  };
  const scheduled = Number(rawScheduledSummary?.data?.Scheduled ?? data.result?.Scheduled) || 0;

  const customStatusPayload = rawCustomStatusSummary?.data || rawCustomStatusSummary || {};
  const customStatusCounts = customStatusPayload.customStatusCounts || data.customStatusCounts || customStatusPayload || {};

  return {
    totalDataAssetRuns: {
      total: Number(data.totalDataAssetRuns) || 0,
      difference: Number(data.totalDataAssetRunsLast1hr) || 0,
      trend: getTrend(Number(data.totalDataAssetRunsLast1hr) || 0),
    },
    uniqueDataAssetCount: {
      total: Number(data.uniqueDataAssetCount) || 0,
      difference: Number(data.uniqueDataAssetCountLast1hr) || 0,
      trend: getTrend(Number(data.uniqueDataAssetCountLast1hr) || 0),
    },
    successful: getKpi('Successful'),
    aborted: getKpi('Aborted'),
    inProgress: getKpi('In Progress'),
    failed: getKpi('Failed'),
    shortRunCount: {
      total: Number(data.shortRunCount) || 0,
      difference: 0,
      trend: 'NO_CHANGE',
    },
    scheduled: {
      total: scheduled,
      difference: Number(resultLast1hr.Scheduled) || 0,
      trend: getTrend(Number(resultLast1hr.Scheduled) || 0),
    },
    totalJobs: { total: totalJobs, difference: totalJobsLastHour, trend: getTrend(totalJobsLastHour) },
    ...CUSTOM_STATUS_TILES.reduce<Record<string, { total: number; difference: number; trend: string }>>(
      (customStatuses, tile) => {
        const status = tile.key.toUpperCase();
        customStatuses[tile.summaryKey] = {
          total: Number(customStatusCounts[status]) || 0,
          difference: 0,
          trend: 'NO_CHANGE',
        };
        return customStatuses;
      },
      {},
    ),
  };
};

const formatDate = (value: any) => {
  if (!value) return '--';
  const [year, month, day] = String(value).split('-');
  return year && month && day ? `${month}-${day}-${year}` : String(value);
};

const formatTime = (value: any) => {
  if (!value) return '--';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const STATUS_COLORS: Record<string, string> = {
  successful: '#01C008',
  succeeded: '#01C008',
  success: '#01C008',
  aborted: '#FE2626',
  abort: '#FE2626',
  failed: '#FE2626',
  inprogress: '#ECB914',
  'in progress': '#ECB914',
  delayed: '#FF38B0',
  scheduled: '#9E9E9E',
};

const DataAssetStatusChip = ({ row }: any) => {
  if (row.isParent) return '';

  const status = String(row.execution_status || '--');
  const color = STATUS_COLORS[status.toLowerCase()] || '#9E9E9E';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1.5,
        py: 0.5,
        backgroundColor: color,
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: '24px',
        borderRadius: '4px',
        minWidth: '70px',
        width: '100%',
        textAlign: 'center',
      }}
    >
      {status.toUpperCase()}
    </Box>
  );
};

const DataAssets = () => {
  const theme = useTheme();
  const p = theme.palette;
  const [controller] = usePepwiseAuthController();
  const { role } = controller;
  const isMetadataUpdatesAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);
  const navigate = useNavigate();
  const { setRightContextContent } = useHeader();
  const {
    banner: pipelineBanner,
    isVisible: isPipelineBannerVisible,
    dismiss: dismissPipelineBanner,
  } = useBanner({ dashboardUrl: '/dataOps/integratedJobMonitoring/dataAssets' });
  const [screenCapturing, setScreenCapturing] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());
  const [isExportWarningOpen, setIsExportWarningOpen] = useState(false);
  const [selectedAdditionalFilters, setSelectedAdditionalFilters] = useState<string[]>([]);

  const handleToggleAdditionalFilter = (filterId: string, checked: boolean) => {
    setSelectedAdditionalFilters((previous) => {
      if (filterId === 'all') {
        const allFilterIds = ['scheduleCategory', 'scheduledForNext', 'sourceCategory', 'sourceType', 'sourceSubcategory', 'orchestratorScheduler', 'dataAssetName', 'department', 'capability', 'technology', 'subscriptionName', 'resourceGroup', 'resourceName', 'sustainOwner', 'sustainProdDate'];
        return checked ? allFilterIds : [];
      }
      return checked
        ? [...previous.filter((id) => id !== filterId), filterId]
        : previous.filter((id) => id !== filterId);
    });
  };

  const handleToggleAllAdditional = (checked: boolean) => {
    const allFilterIds = ['scheduleCategory', 'scheduledForNext', 'sourceCategory', 'sourceType', 'sourceSubcategory', 'orchestratorScheduler', 'dataAssetName', 'department', 'capability', 'technology', 'subscriptionName', 'resourceGroup', 'resourceName', 'sustainOwner', 'sustainProdDate'];
    setSelectedAdditionalFilters(checked ? allFilterIds : []);
  };

  const {
    activeTile,
    activeBottomTab,
    setActiveBottomTab,
    page,
    pageSize,
    sortingInfo,
    summary,
    scheduledSummary,
    customStatusSummary,
    runDistribution,
    runOvertimeTrend,
    runOvertimeCards,
    jobTypeByStatusData,
    scheduleByHourData,
    isChartLoading,
    tableData,
    totalElements,
    isTotalCountLoading,
    isLoading,
    isExporting,
    appliedFilters,
    filterOptions,
    loadingFilterFields,
    isInitialFilterLoading,
    groupBy,
    setGroupBy,
    loadAllDropDowns,
    handleApplyFilters,
    setPage,
    setPageSize,
    setSortingInfo,
    handleTileClick,
    handleExport,
    getExportRecordCount,
    columnFilters,
    handleColumnFiltersChange,
    getColumnFilterValues,
    columnFilterModel,
    isKpiLoading,
    isCustomStatusKpiLoading,
    isScheduledKpiLoading,
    manualSaveFilters,
    resetToSavedFilters,
    hasSavedFilters,
    savedDefaultFilters,
  } = useDataAssets();

  useEffect(() => {
    setRightContextContent(
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton
          onClick={() => setIsFilterDrawerOpen(true)}
          sx={{ position: 'relative', p: '0px !important' }}
          aria-label="Open filters"
        >
          <FilterIcon />
        </IconButton>
      </Box>,
    );

    return () => setRightContextContent(null);
  }, [setRightContextContent]);

  useEffect(() => {
    setExpandedGroups(new Set());
  }, [groupBy]);

  const filterFields: Array<{
    label: string;
    placeholder: string;
    options: GenericOption[];
    value: GenericOption[];
    field: keyof FilterState;
    loading?: boolean;
    initialOptionLimit?: number;
  }> = [
    {
      label: 'Programs',
      placeholder: 'Select programs...',
      options: toOptions(filterOptions.program || []),
      value: appliedFilters.program || [],
      field: 'program',
      loading: !!loadingFilterFields.program,
      initialOptionLimit: 100,
    },
    {
      label: 'Domain',
      placeholder: 'Select domains...',
      options: toOptions(filterOptions.domain || []),
      value: appliedFilters.domain || [],
      field: 'domain',
      loading: !!loadingFilterFields.domain,
      initialOptionLimit: 100,
    },
    {
      label: 'Sectors',
      placeholder: 'Select sectors...',
      options: toOptions(filterOptions.sector || []),
      value: appliedFilters.sector || [],
      field: 'sector',
      loading: !!loadingFilterFields.sector,
    },
    {
      label: 'Market',
      placeholder: 'Select markets...',
      options: toOptions(filterOptions.market || []),
      value: appliedFilters.market || [],
      field: 'market',
      loading: !!loadingFilterFields.market,
      initialOptionLimit: 100,
    },
    {
      label: 'Data Asset Name',
      placeholder: 'Select data asset names...',
      options: toOptions(filterOptions.dataAssetName || []),
      value: appliedFilters.dataAssetName || [],
      field: 'dataAssetName',
      loading: !!loadingFilterFields.dataAssetName,
      initialOptionLimit: 100,
    },
    {
      label: 'Job Type',
      placeholder: 'Select job types...',
      options: toOptions(filterOptions.jobType || []),
      value: appliedFilters.jobType || [],
      field: 'jobType',
      loading: !!loadingFilterFields.jobType,
    },
    {
      label: 'Source',
      placeholder: 'Select sources...',
      options: toOptions(filterOptions.sourceType || []),
      value: appliedFilters.sourceType || [],
      field: 'sourceType',
      loading: !!loadingFilterFields.sourceType,
    },
    { label: 'Schedule Category', placeholder: 'Select schedule categories...', options: toOptions(filterOptions.scheduleCategory || []), value: appliedFilters.scheduleCategory || [], field: 'scheduleCategory', loading: !!loadingFilterFields.scheduleCategory },
    { label: 'Source Environment', placeholder: 'Select source environments...', options: toOptions(filterOptions.sourceCategory || []), value: appliedFilters.sourceCategory || [], field: 'sourceCategory', loading: !!loadingFilterFields.sourceCategory },
    { label: 'Source Sub Category', placeholder: 'Select source subcategories...', options: toOptions(filterOptions.sourceSubcategory || []), value: appliedFilters.sourceSubcategory || [], field: 'sourceSubcategory', loading: !!loadingFilterFields.sourceSubcategory },
    { label: 'Orchestrator/Scheduler', placeholder: 'Select orchestrators/schedulers...', options: toOptions(filterOptions.orchestratorScheduler || []), value: appliedFilters.orchestratorScheduler || [], field: 'orchestratorScheduler', loading: !!loadingFilterFields.orchestratorScheduler },
    { label: 'Business Stream', placeholder: 'Select business streams...', options: toOptions(filterOptions.businessStream || []), value: appliedFilters.businessStream || [], field: 'businessStream', loading: !!loadingFilterFields.businessStream },
    { label: 'Department', placeholder: 'Select departments...', options: toOptions(filterOptions.department || []), value: appliedFilters.department || [], field: 'department', loading: !!loadingFilterFields.department },
    { label: 'Capability', placeholder: 'Select capabilities...', options: toOptions(filterOptions.capability || []), value: appliedFilters.capability || [], field: 'capability', loading: !!loadingFilterFields.capability },
    { label: 'Layer', placeholder: 'Select layers...', options: toOptions(filterOptions.layer || []), value: appliedFilters.layer || [], field: 'layer', loading: !!loadingFilterFields.layer },
    { label: 'Technology', placeholder: 'Select technologies...', options: toOptions(filterOptions.technology || []), value: appliedFilters.technology || [], field: 'technology', loading: !!loadingFilterFields.technology },
    { label: 'Bronze Table', placeholder: 'Select bronze tables...', options: toOptions(filterOptions.bronzeTableName || []), value: appliedFilters.bronzeTableName || [], field: 'bronzeTableName', loading: !!loadingFilterFields.bronzeTableName, initialOptionLimit: 100 },
    { label: 'Silver Table', placeholder: 'Select silver tables...', options: toOptions(filterOptions.silverTableName || []), value: appliedFilters.silverTableName || [], field: 'silverTableName', loading: !!loadingFilterFields.silverTableName, initialOptionLimit: 100 },
    { label: 'Gold Table', placeholder: 'Select gold tables...', options: toOptions(filterOptions.goldTableName || []), value: appliedFilters.goldTableName || [], field: 'goldTableName', loading: !!loadingFilterFields.goldTableName, initialOptionLimit: 100 },
    { label: 'Sustain Owner', placeholder: 'Select sustain owners...', options: toOptions(filterOptions.sustainOwner || []), value: appliedFilters.sustainOwner || [], field: 'sustainOwner', loading: !!loadingFilterFields.sustainOwner },
    { label: 'Sustain PROD Date', placeholder: 'Select sustain PROD dates...', options: toOptions(filterOptions.sustainProdDate || []), value: appliedFilters.sustainProdDate || [], field: 'sustainProdDate', loading: !!loadingFilterFields.sustainProdDate },
    { label: 'Resource Name', placeholder: 'Select resource names...', options: toOptions(filterOptions.resourceName || []), value: appliedFilters.resourceName || [], field: 'resourceName', loading: !!loadingFilterFields.resourceName, initialOptionLimit: 100 },
    { label: 'Resource Group', placeholder: 'Select resource groups...', options: toOptions(filterOptions.resourceGroup || []), value: appliedFilters.resourceGroup || [], field: 'resourceGroup', loading: !!loadingFilterFields.resourceGroup, initialOptionLimit: 100 },
    { label: 'Subscription Name', placeholder: 'Select subscription names...', options: toOptions(filterOptions.subscriptionName || []), value: appliedFilters.subscriptionName || [], field: 'subscriptionName', loading: !!loadingFilterFields.subscriptionName },
  ];

  const transformedSummary = useMemo(
    () => getAssetSummary(summary, scheduledSummary, customStatusSummary),
    [summary, scheduledSummary, customStatusSummary],
  );

  const columns = useMemo(() => {
    const textColumn = (key: string, name: string, width = 180, value?: (row: any) => any) => ({
      key,
      name,
      width,
      formatter: ({ row }: any) => {
        if (row.isParent) return '';
        return value?.(row) || row[key] || '--';
      },
      sortable: true,
      resizable: true,
    });

    const isScheduled = activeTile === 'Scheduled';

    return [
      textColumn('date', isScheduled ? 'SCHEDULED DATE' : 'DATE', 120, (row) => formatDate(row.date)),
      textColumn('hour', isScheduled ? 'SCHEDULED HOUR' : 'HOUR', 90),
      textColumn('expected_start_time', 'EXPECTED START TIME', 230),
      ...(isScheduled ? [
        textColumn('last_run_start_time', "TODAY'S RUN START", 230),
        textColumn('schedule_missed', 'SCHEDULE MISSED', 230, (row) =>
          row.schedule_missed === true ? 'True' : row.schedule_missed === false ? 'False' : '--'),
        textColumn('origin', 'ORIGIN', 230),
        textColumn('destination', 'DESTINATION', 230),
      ] : []),
      textColumn('job_type', 'JOB TYPE', 140, (row) => row.job_type || row.jobType),
      textColumn('job_id', 'JOB ID', 220, (row) => row.job_id || row.jobId),
      ...(!isScheduled ? [{
        ...textColumn('run_id', 'JOB RUN URL', 220, (row) => row.run_url || row.adfUrl || row.run_id || row.runId),
        formatter: ({ row }: any) => {
          if (row.isParent) return '';
          const runUrl = row.run_url || row.adfUrl;
          const displayValue = runUrl || row.run_id || row.runId;
          return runUrl
            ? <a href={runUrl} target="_blank" rel="noopener noreferrer">{displayValue}</a>
            : displayValue || '--';
        },
      }] : []),
      textColumn('job_name', 'JOB NAME', 200, (row) => row.job_name || row.jobName),
      {
        ...textColumn('execution_status', 'STATUS', 150),
        renderCell: DataAssetStatusChip,
        formatter: DataAssetStatusChip,
        cellClass: 'status-center',
      },
      ...(!isScheduled ? [
        textColumn('start_time', 'START TIME', 180, (row) => formatTime(row.start_time || row.actualStartTime)),
        textColumn('end_time', 'END TIME', 180, (row) => formatTime(row.end_time || row.actualEndTime)),
      ] : []),
      textColumn('source_type', 'SOURCE', 150),
      textColumn('schedule_info', 'SCHEDULE INFO', 300),
      textColumn('timezone', 'TIMEZONE', 150),
      textColumn('program_name', 'PROGRAM', 160, (row) => row.program_name || row.programName),
      textColumn('sector', 'SECTOR', 150),
      textColumn('duration_ms', 'RUN TIME MIN', 150, (row) =>
        row.duration_ms == null ? '--' : (Number(row.duration_ms) / 60000).toFixed(0)),
      textColumn('estimated_end_time', 'EXPECTED END TIME', 200, (row) => formatTime(row.estimated_end_time)),
      textColumn('avg_duration_ms', 'AVG RUN TIME MIN', 180, (row) =>
        row.avg_duration_ms == null ? '--' : (Number(row.avg_duration_ms) / 60000).toFixed(0)),
      textColumn('total_exe_count', 'TOTAL RECORDS', 160),
      textColumn('is_recurrence', 'RECURRING', 130),
      textColumn('recurrence_per_day', 'RECURRENCES PER DAY', 200),
      textColumn('trigger_name', 'TRIGGER NAME', 170),
      textColumn('trigger_type', 'TRIGGER TYPE', 170),
      textColumn('data_asset_name', 'DATA ASSET NAME', 200),
      textColumn('market', 'MARKET', 180),
      textColumn('gold_table_name', 'GOLD TABLE NAME', 200),
      textColumn('silver_table_name', 'SILVER TABLE NAME', 200),
      textColumn('bronze_table_name', 'BRONZE TABLE NAME', 210),
      textColumn('subscription_name', 'SUBSCRIPTION NAME', 210),
      textColumn('subscription_id', 'SUBSCRIPTION ID', 200),
      textColumn('resource_group', 'RESOURCE GROUP', 200),
      textColumn('resource_name', 'RESOURCE NAME', 200),
      textColumn('job_no', 'JOB NUMBER', 160),
      textColumn('application_name', 'APPLICATION NAME', 200),
      textColumn('folder_name', 'FOLDER NAME', 180),
      textColumn('domain', 'DOMAIN', 180),
      textColumn('productShortName', 'PRODUCT SHORT NAME', 220),
      textColumn('program_job_name', 'PROGRAM JOB NAME', 200),
      textColumn('gdo', 'GDO', 150),
      textColumn('layer', 'LAYER', 150),
      textColumn('success_count', 'Success Count', 160, (row) => row.success_count),
      textColumn('failed_count', 'Failed Count', 160, (row) => row.failed_count)
    ];
  }, [activeTile]);

  const displayTableData = useMemo(() => {
    if (groupBy === 'none') return tableData;

    return tableData.flatMap((row: any) => {
      if (!row?.isParent) return [row];
      const groupKey = String(row[groupBy] ?? '');
      const isExpanded = expandedGroups.has(groupKey);
      const children = Array.isArray(row.children) ? row.children : [];

      return [
        { ...row, isExpanded },
        ...(isExpanded
          ? children.map((child: any) => ({
              ...child,
              isParent: false,
              [groupBy]: child[groupBy] ?? row[groupBy],
            }))
          : []),
      ];
    });
  }, [tableData, groupBy, expandedGroups]);

  const displayColumns = useMemo(() => {
    if (groupBy === 'none') return columns;

    const groupedColumn = columns.find((column: any) => column.key === groupBy);
    if (!groupedColumn) return columns;

    const groupedFirstColumn = {
      ...groupedColumn,
      minWidth: 250,
      maxWidth: 750,
      width: 500,
      formatter: ({ row }: any) => (
        <GroupedTableFormatter
          row={row}
          groupByColumn={groupBy}
          groupByLabel={String(row[groupBy] || 'Unassigned')}
          countLabel="Jobs"
          onToggle={() => {
            const groupValue = String(row[groupBy] ?? '');
            setExpandedGroups((current) => {
              const next = new Set(current);
              if (next.has(groupValue)) next.delete(groupValue);
              else next.add(groupValue);
              return next;
            });
          }}
        />
      ),
    };

    return [
      groupedFirstColumn,
      ...columns.filter((column: any) => column.key !== groupBy),
    ];
  }, [columns, groupBy]);

  const columnFilterController = useColumnFilters({
    columns: displayColumns,
    rows: displayTableData,
    mode: 'server',
    initialFilterModel: columnFilterModel || columnFilterPayloadToModel(columnFilters),
    initialSortModel: sortingInfo.sortBy
      ? [{ columnKey: sortingInfo.sortBy, direction: sortingInfo.sortDir as SortDirection }]
      : [],
    onChange: ({ filterModel, sortModel }) => {
      const nextSortingInfo = sortModel[0]
        ? { sortBy: sortModel[0].columnKey, sortDir: sortModel[0].direction }
        : { sortBy: '', sortDir: '' };
      handleColumnFiltersChange(toColumnFiltersPayload(filterModel), nextSortingInfo, filterModel);
    },
  });

  const handleSaveAsDefault = async (filters: FilterState) => {
    handleApplyFilters(filters);
    await manualSaveFilters(filters);
  };

  const handleChartCardClick = (key: number) => {
    setActiveBottomTab(key);
  };

  const handleBackToTable = (tile: string) => {
    setActiveBottomTab(null);
    handleTileClick(tile);
  };

  const handleResetToDefault = async () => {
    const result = await resetToSavedFilters();
    if (!result?.success || !result.filters) return;

    handleApplyFilters(result.filters);
    await loadAllDropDowns(true, result.filters);
  };

  const handleExportClick = async () => {
    const exportLimit = Number(filterOptions?.exportLimit);
    if (exportLimit) {
      const freshCount = await getExportRecordCount();
      if (freshCount > exportLimit) {
        setIsExportWarningOpen(true);
      } else {
        await handleExport();
      }
    } else {
      await handleExport();
    }
  };

  const handleScreenshot = async () => {
    const element = document.getElementById('data-assets-capture-area');
    if (!element) return;
    setScreenCapturing(true);
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        scrollY: -window.scrollY,
        ignoreElements: (el: any) => ['exclude-camera', 'exclude-export'].includes(el.id),
      });
      setPreviewImage(canvas.toDataURL('image/png'));
      setOpenPreview(true);
    } catch (error) {
      console.error('Data Assets screenshot failed:', error);
    } finally {
      setScreenCapturing(false);
    }
  };

  const headerOffset = { xs: '87px', sm: '67px', md: '77px' };

  return (
    <>
      {isPipelineBannerVisible && (
        <Box
          sx={{
            mt: headerOffset,
            width: '100%',
            '& .MuiPaper-root': {
              width: '100%',
              margin: '0 !important',
              boxSizing: 'border-box',
              borderRadius: 0,
            },
          }}
        >
          <Banner
            severity={(pipelineBanner?.severity || pipelineBanner?.contentType as any) || 'info'}
            title={pipelineBanner?.title}
            message={pipelineBanner?.text ?? ''}
            onDismiss={dismissPipelineBanner}
          />
        </Box>
      )}
      <PageWrapper sx={{ ...(isPipelineBannerVisible ? { mt: 0, pt: { xs: 2, md: 2, lg: 2 } } : {}), minHeight: 'calc(100vh - 120px)', backgroundColor: p.backgroundLight.sideNavBgHover, position: 'relative' }}>
        <Box id="data-assets-capture-area" sx={{ pt: isPipelineBannerVisible ? 0 : 1, position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, width: '100%' }}>
            <SummaryTiles
              summary={transformedSummary}
              gridColumns={{ xs: 1, sm: 2, md: 4, lg: 4 }}
              activeTile={activeTile}
              onTileClick={handleBackToTable}
              isLoading={isKpiLoading}
              isScheduledLoading={isScheduledKpiLoading}
              isCustomStatusLoading={isCustomStatusKpiLoading}
              tilesConfig={DATA_ASSETS_TILES}
              tooltipLabel="over the selected date range"
              tileLabel={(tileKey: string) => {
                if (tileKey === 'Scheduled') {
                  const selected = appliedFilters.scheduledForNext?.[0];
                  const selectedLabel = selected ? selected.label : '';
                  return selectedLabel.replace(/\bhr\b/i, 'Hr');
                }
                if (tileKey === 'SHORT RUN' || CUSTOM_STATUS_OPTIONS.includes(tileKey)) return '';
                return 'From Last 1 Hr';
              }}
              showTrend
            />
            {!isLoading && (
              <CaptureExportControls
                onScreenshot={handleScreenshot}
                screenCapturing={screenCapturing}
                onExport={handleExportClick}
                isExporting={isExporting}
                isVisible
                showExport
                type="pipeline"
                exportTooltip="Max 200K records will be exported at a Time."
              />
            )}
          </Box>

          <Box sx={{ mt: 1, backgroundColor: p.white.main, p: 2 }}>
            {activeBottomTab === null ? (
              <CustomizedTable
                tableTitle={getDataAssetTableTitle(activeTile)}
                tableData={displayTableData}
                total={totalElements}
                isTotalCountLoading={isTotalCountLoading}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                onSorting={setSortingInfo}
                sortingInfo={sortingInfo}
                defaultColumns={getDefaultColumns(activeTile, groupBy).filter((columnName) => columnName !== 'CUSTOM STATUS')}
                tableType={`pipeline-run-status-data-assets-${activeTile}`}
                columns={displayColumns}
                hasGroupBy
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
                includeGroupByMenuColumns={DATA_ASSET_GROUP_BY_COLUMNS}
                onMetadataUpdatesClick={isMetadataUpdatesAllowed
                  ? () => navigate('/dataOps/integratedJobMonitoring/metadataUpdates')
                  : undefined}
                isFetching={isLoading}
                isPrevNextPagination
                enableColumnFilter
                columnFilterController={columnFilterController}
                getColumnFilterValues={getColumnFilterValues}
              />
            ) : activeBottomTab === 0 ? (
              <RunDistribution data={runDistribution || undefined} isLoading={isChartLoading} />
            ) : activeBottomTab === 1 ? (
              <RunOvertimeTrend
                xAxisData={runOvertimeTrend?.xAxisData || []}
                series={runOvertimeTrend?.series || []}
                cards={runOvertimeCards}
                isLoading={isChartLoading}
                isCardsLoading={isChartLoading}
              />
            ) : activeBottomTab === 2 ? (
              <DashboardSplitView
                isLoading={isChartLoading}
                title="Job Type By Status"
                chartCategories={jobTypeByStatusData?.categories || []}
                chartSeries={jobTypeByStatusData?.series || []}
                tableColumns={jobTypeByStatusData?.tableColumns || []}
                tableData={jobTypeByStatusData?.tableData || []}
              />
            ) : (
              <DashboardSplitView
                isLoading={isChartLoading}
                isScheduleByHour
                title="Schedule By Hour"
                chartCategories={scheduleByHourData?.categories || []}
                chartSeries={scheduleByHourData?.series || []}
                tableColumns={scheduleByHourData?.tableColumns || []}
                tableData={scheduleByHourData?.tableData || []}
              />
            )}
          </Box>

          <Box
            id="data-assets-bottom-tabs"
            sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              width: { xs: '100%', md: '66.67%' },
              mt: 2,
            }}
          >
            {CHART_CARDS.map((card) => {
              const isActive = activeBottomTab === card.key;
              const { Icon } = card;
              const iconColor = isActive ? p.primary.main : p.white.main;

              return (
                <Box
                  key={card.key}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isActive}
                  onClick={() => handleChartCardClick(card.key)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleChartCardClick(card.key);
                    }
                  }}
                  sx={{
                    flex: { xs: '1 1 100%', sm: '1', md: '1' },
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    py: { xs: 1.5, md: 2.5 },
                    px: { xs: 1, md: 2 },
                    cursor: 'pointer',
                    backgroundColor: isActive ? p.white.main : p.primary.main,
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <Box sx={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                    <Icon color={iconColor} />
                  </Box>
                  <Box component="span" sx={{ color: iconColor, fontWeight: 500, textAlign: 'right', lineHeight: 1.2 }}>
                    {card.label}
                  </Box>
                  <Box component="span" sx={{ color: iconColor, textAlign: 'right' }}>
                    {card.sub}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </PageWrapper>
      <FilterDrawer
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        onApply={handleApplyFilters}
        onSaveAsDefault={handleSaveAsDefault}
        onResetToDefault={handleResetToDefault}
        hasSavedFilters={hasSavedFilters}
        savedDefaultFilters={savedDefaultFilters}
        currentFilters={{ ...appliedFilters, miscellaneousSectors: appliedFilters.miscellaneousSectors || [] }}
        filterFields={filterFields}
        customFilterFields={customFilterFields}
        onDropdownOpen={(draftFilters: FilterState, field: keyof FilterState) => loadAllDropDowns(false, draftFilters, field)}
        additionalFilterOptions={filterFields
          .filter(f => !['program', 'sector', 'market', 'jobType', 'businessUnit', 'appName', 'miscellaneousSectors', 'sourceType'].includes(f.field as string))
          .map(f => ({
            id: f.field as string,
            label: f.label,
            level: (['sourceSubcategory', 'orchestratorScheduler', 'goldTableName'].includes(f.field as string)) ? 2 : 1
          }))
        }
        selectedAdditionalFilters={selectedAdditionalFilters}
        onToggleAdditionalFilter={handleToggleAdditionalFilter}
        onToggleAllAdditional={handleToggleAllAdditional}
        isInitialLoading={isInitialFilterLoading}
      />
      <ExportLimitWarningDialog
        isOpen={isExportWarningOpen}
        limit={Number(filterOptions?.exportLimit) || 500}
        handleClose={() => setIsExportWarningOpen(false)}
        handleConfirm={handleExport}
      />
      <ImagePreview
        open={openPreview}
        image={previewImage}
        onClose={() => setOpenPreview(false)}
        title="Data Assets"
      />
    </>
  );
};

export default DataAssets;
